<template>
  <Dropdown
    :content-style="{ width: '280px' }"
    trigger="hover"
    :gap="gap"
    :disabled="disabled"
    :position="'bottomCenter'"
    class="top-user-dropdown"
  >
    <template #trigger>
      <div class="top-user-dropdown-trigger">
        <slot name="trigger" />
      </div>
    </template>
    <template #content>
      <div class="top-user-dropdown-content">
        <div class="top-user-dropdown-content-info">
          <AvatarUploader
            :value="userStore.user?.logo_url || ''"
            @update="userStore.fetchUserInfo"
            :editable="hasActionPermission(GENERIC_ACTION_KEYS.CHANGE_AVATAR)"
          />
          <div class="account-name">{{ userStore.user?.account_name }}</div>
          <div class="right-type">{{ displayRightTypeName }}</div>
        </div>

        <div class="divider"></div>

        <div class="top-user-dropdown-content-menu">
          <div
            v-for="item in centerNavList"
            :key="item.label"
            :class="['option-item', item.inVisible?.() ? 'hidden' : '']"
          >
            <Link :href="patchDomain(item.href)" :target="linkTarget">
              {{ item.label }}
              <RightIcon class="right-icon w-[16px] h-[16px]" />
            </Link>
          </div>
        </div>

        <div class="divider"></div>

        <div class="top-user-dropdown-content-bottom">
          <div v-if="canSwitchAccount" class="option-item" @click="handleSwitchAccount">
            {{ $t('qie-huan-zhang-hao') }}
            <RightIcon class="right-icon w-[16px] h-[16px]" />
          </div>
          <div v-else-if="canCreateTeam" class="option-item" @click="handleCreateTeam">
            {{ $t('chuang-jian-tuan-dui') }}
            <RightIcon class="right-icon w-[16px] h-[16px]" />
          </div>
          <div v-if="canLogout" class="option-item" @click="handleLogout">
            {{ $t('tui-chu-deng-lu') }}
            <RightIcon class="right-icon w-[16px] h-[16px]" />
          </div>
        </div>
      </div>
    </template>
  </Dropdown>
</template>

<script setup lang="ts">
import { useEnumDictionary } from '@rendora/common/composables/useEnumDictionary';
import { useCenterNav } from '@rendora/common/composables/useNav';
import { GENERIC_ACTION_KEYS } from '@rendora/common/entities/app_cfg';
import { showSwitchAccountModal } from '@rendora/common/features/switch-account';
import { getI18nService } from '@rendora/common/services/i18n';
import { usePermissionStore } from '@rendora/common/store/permission';
import { useUserStore } from '@rendora/common/store/user';
import { IRightsType, USER_ADMIN } from '@rendora/common/types/enums';
import { getAreaConfig, isPrivate, isPureHost } from '@rendora/common/utils/config';
import { getLocation } from '@rendora/common/utils/nuxtAdapter';
import { getPlatformJumpLink } from '@rendora/common/utils/url';
import RightIcon from '@rendora/design/assets/arrow.svg?component';
import { Dropdown } from '@rendora/design/components/dropdown/index';
import { Link } from '@rendora/design/components/link/index';
import { computed } from 'vue';
import AvatarUploader from './AvatarUploader.vue';

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    gap?: [number, number];
  }>(),
  {
    disabled: false,
    gap: () => [20, 6],
  },
);

const patchDomain = (path?: string) => {
  const location = getLocation();
  const isWorkspace = location.pathname.includes('workspace');
  if (isWorkspace) return path;
  const { domain } = getAreaConfig();
  return `${domain}/workspace${path}`;
};

const $t = getI18nService().t;
const userStore = useUserStore();
const { hasActionPermission, permissionConfig } = usePermissionStore();

const rightsTypeMap = useEnumDictionary('rights_type');
const { centerNavList } = useCenterNav(true);

const displayRightTypeName = computed(() => {
  const rightsType = userStore.user?.rights_type;
  return rightsType ? rightsTypeMap[rightsType] : '';
});

const linkTarget = computed(() => (isPureHost() || isPrivate() ? '_self' : '_blank'));

const canSwitchAccount = computed(
  () =>
    userStore.accountList.length > 1 && hasActionPermission(GENERIC_ACTION_KEYS.SWITCH_ACCOUNTS),
);

const canCreateTeam = computed(
  () =>
    hasActionPermission(GENERIC_ACTION_KEYS.CREATE_TRY_TEAM) &&
    !userStore.accountList.some(
      item =>
        Number(item.rights_type) === IRightsType.TEAM_TASTE &&
        Number(item.permission) !== USER_ADMIN.NORMAL,
    ),
);

const canLogout = computed(() => hasActionPermission(GENERIC_ACTION_KEYS.LOGOUT));

const isRunningInFeishuClient = (): boolean => {
  const userAgent = navigator.userAgent;
  return userAgent.includes('Lark') || userAgent.includes('Feishu');
};

const handleLogout = () => {
  if (isRunningInFeishuClient()) {
    window.location.reload();
    return;
  }
  userStore.logout(permissionConfig?.config?.logout_url || '/login');
};

const handleSwitchAccount = () => {
  showSwitchAccountModal({ title: $t('qie-huan-zhang-hao'), width: '500px' });
};

const handleCreateTeam = () => {
  location.href = getPlatformJumpLink('/team-taste-create?step=1');
};
</script>

<style scoped lang="scss">
.top-user-dropdown {
  :deep(.dropdown-trigger.disabled) {
    opacity: 1;
  }
}
.top-user-dropdown-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  border: 1px solid var(--x-back-4);
  border-radius: var(--x-radius-medium);
  background-color: var(--x-white-1);
}

.top-user-dropdown-content-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 248px;
  gap: 4px;
  padding: 8px 0;
  overflow: hidden;

  > div:first-child {
    border-width: 0;
  }
}

.account-name {
  font-weight: var(--x-weight-regular);
  font-size: var(--x-text-18-size);
  line-height: var(--x-text-18-line-height);
  color: var(--x-text-1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.right-type {
  font-weight: var(--x-weight-regular);
  font-size: var(--x-text-12-size);
  line-height: var(--x-text-12-line-height);
  color: var(--x-text-3);
  text-align: center;
}

.divider {
  width: 100%;
  height: 1px;
  margin: 8px 0;
  background-color: var(--x-back-4);
}

.top-user-dropdown-content-menu,
.top-user-dropdown-content-bottom {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.option-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 36px;
  padding: 8px 16px;
  border-radius: var(--x-radius-medium);
  font-weight: var(--x-weight-regular);
  font-size: var(--x-text-14-size);
  line-height: var(--x-text-14-line-height);
  color: var(--x-text-1);
  cursor: pointer;

  &.hidden {
    display: none;
  }

  .right-icon {
    color: var(--x-text-5);
    display: none;
  }

  &:hover {
    background-color: var(--x-back-4);

    .right-icon {
      display: block;
    }
  }

  a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    text-align: left;
    color: var(--x-text-1);
  }
}
</style>
